// src/pages/shop/ShopReportJournal.jsx
//
// Вкладка «Отчёт» — журнал продаж. Фильтр по периоду, подгрузка по nextCursor,
// тап по продаже раскрывает детали (кто продал, что, за сколько).
// Кнопка «PDF отчёт» ведёт в генератор PDF.

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { sales as salesApi } from '../../api';
import BottomNav from '../../components/BottomNav';
import Icon from '../../components/Icon';
import { formatMoney } from '../../data/currencies';
import { useMyCurrency } from '../../hooks/useMyShop';
import { useReloadOnFocus } from '../../hooks/useReloadOnFocus';
import { useShopNav } from './ShopHome';

const DAY = 86400000;
const PERIODS = [
  { key: 'today', days: 0 },
  { key: 'week',  days: 7 },
  { key: 'month', days: 30 },
];

function periodFrom(key) {
  const now = new Date();
  if (key === 'today') { const d = new Date(now); d.setHours(0, 0, 0, 0); return d.toISOString(); }
  if (key === 'week')  return new Date(now.getTime() - 7 * DAY).toISOString();
  return new Date(now.getTime() - 30 * DAY).toISOString();
}

export default function ShopReportJournal() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const navItems = useShopNav();
  const currency = useMyCurrency();

  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [period, setPeriod] = useState('month');
  const [expanded, setExpanded] = useState(null);

  const params = useCallback((cursor) => ({
    from: periodFrom(period),
    limit: 30,
    ...(cursor ? { cursor } : {}),
  }), [period]);

  const load = useCallback(() => {
    setLoading(true); setExpanded(null);
    salesApi.journal(params(null))
      .then(r => { setItems(r.items || []); setNextCursor(r.nextCursor || null); })
      .catch(() => { setItems([]); setNextCursor(null); })
      .finally(() => setLoading(false));
  }, [params]);

  useEffect(() => { load(); }, [load]);
  useReloadOnFocus(() => { load(); });

  const loadMore = () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    salesApi.journal(params(nextCursor))
      .then(r => { setItems(prev => [...prev, ...(r.items || [])]); setNextCursor(r.nextCursor || null); })
      .catch(() => {})
      .finally(() => setLoadingMore(false));
  };

  const whoOf = (s) => s.employee ? s.employee.name : t('report.owner');
  const fmtTime = (iso) => new Date(iso).toLocaleString(i18n.language || undefined, {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title" style={{ flex: 1 }}>{t('report.title')}</h1>
        <button onClick={() => navigate('/shop/reports')} className="btn btn-soft" style={{ padding: '8px 12px', fontSize: 13 }}>
          <Icon name="fileText" size={14} />
          <span style={{ marginLeft: 6 }}>{t('reports.pdfReport')}</span>
        </button>
      </div>

      <div style={{ padding: '0 20px' }}>
        {/* Период */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
          {PERIODS.map(p => (
            <button key={p.key} onClick={() => setPeriod(p.key)}
              style={{
                flex: 1, padding: '8px 0', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                borderRadius: 999, fontFamily: 'var(--font-display)',
                border: '1px solid ' + (period === p.key ? 'var(--brand-primary)' : 'var(--border)'),
                background: period === p.key ? 'var(--brand-primary)' : 'var(--surface)',
                color: period === p.key ? '#fff' : 'var(--fg)',
              }}>
              {t(`common.${p.key}`)}
            </button>
          ))}
        </div>

        {loading && <div className="loader" style={{ padding: 24 }}><div className="spinner" /></div>}

        {!loading && items.length === 0 && (
          <div className="empty-state">
            <div className="icon"><Icon name="fileText" size={28} stroke={1.5} /></div>
            <p>{t('report.empty')}</p>
          </div>
        )}

        {!loading && items.length > 0 && (
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            {items.map((s, idx) => {
              const open = expanded === s.id;
              return (
                <div key={s.id} style={{ borderTop: idx === 0 ? 'none' : '1px solid var(--border-subtle)' }}>
                  <button onClick={() => setExpanded(open ? null : s.id)}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                    <div style={{ width: 34, height: 34, borderRadius: 'var(--r-sm)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: s.paidByPoints ? 'var(--brand-accent-soft)' : 'var(--success-soft)', color: s.paidByPoints ? 'var(--brand-accent)' : 'var(--success)' }}>
                      <Icon name={s.paidByPoints ? 'starFilled' : 'wallet'} size={16} stroke={s.paidByPoints ? 0 : undefined} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--fg)', fontVariantNumeric: 'tabular-nums' }}>
                        {formatMoney(s.total, currency)}
                      </div>
                      <div style={{ fontSize: 11.5, color: 'var(--fg-faint)', marginTop: 2 }}>
                        {whoOf(s)} · {fmtTime(s.createdAt)}
                      </div>
                    </div>
                    <Icon name={open ? 'chevronDown' : 'chevronRight'} size={15} color="var(--fg-faint)" />
                  </button>

                  {open && (
                    <div style={{ padding: '0 14px 14px 60px', fontSize: 13, color: 'var(--fg-muted)' }}>
                      {/* Позиции */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
                        {(s.items || []).map(it => (
                          <div key={it.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                            <span style={{ flex: 1, minWidth: 0 }}>
                              {it.name}{it.variantName ? ` · ${it.variantName}` : ''}
                              <span style={{ color: 'var(--fg-faint)' }}> × {it.qty}</span>
                            </span>
                            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatMoney(it.salePrice * it.qty, currency)}</span>
                          </div>
                        ))}
                      </div>
                      {s.discountAmount > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--danger)' }}>
                          <span>{t('report.discount')}</span>
                          <span style={{ fontVariantNumeric: 'tabular-nums' }}>−{formatMoney(s.discountAmount, currency)}</span>
                        </div>
                      )}
                      {s.paidByPoints && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--brand-accent)', marginTop: 2 }}>
                          <span>{t('report.paidByPoints')}</span>
                          <span style={{ fontVariantNumeric: 'tabular-nums' }}>−{(s.pointsRedeemed || 0).toLocaleString()} ⭐{s.cashAmount > 0 ? ` + ${formatMoney(s.cashAmount, currency)}` : ''}</span>
                        </div>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, color: 'var(--fg)', marginTop: 6, paddingTop: 6, borderTop: '1px solid var(--border-subtle)' }}>
                        <span>{t('report.total')}</span>
                        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatMoney(s.total, currency)}</span>
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--fg-faint)', marginTop: 6 }}>
                        {t('report.soldBy')}: {whoOf(s)}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {!loading && nextCursor && (
          <button onClick={loadMore} disabled={loadingMore} className="btn btn-secondary" style={{ width: '100%', marginTop: 12 }}>
            {loadingMore ? '…' : t('audit.loadMore')}
          </button>
        )}

        <div style={{ height: 20 }} />
      </div>

      <BottomNav items={navItems} />
    </div>
  );
}
